import {
  Button,
  Form,
  FormSubmitButton,
  LoadingSpinner,
  SelectFormField,
  Typography,
} from "@/components/ui";
import { ActionConfirmationModal } from "@/components/ui/ActionConfirmationModal/ActionConfirmationModal";
import { AppModal, type AppModalSize } from "@/components/ui/AppModal/AppModal";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterIntakeTemplateQuestionsOptions } from "@/lib/data/react-query/matters/matter-intake-template-question";
import {
  useCreateMatterIntakeTemplateResponse,
  useDeleteMatterIntakeTemplateResponse,
  useUpdateMatterIntakeTemplateResponse,
} from "@/lib/data/react-query/matters/matter-intake-template-response";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { queryKeys } from "@/lib/data/static";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type {
  MatterIntakeTemplateQuestion,
  MatterIntakeTemplateResponse,
} from "@/lib/types";
import { modalFormFieldsetClassName } from "@/lib/utils/modal-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { renderIntakeFormField } from "./utils/intake-form-fields";
import {
  createEditIntakeResponseSchema,
  createIntakeResponseSchema,
} from "./utils/intake-schemas";
import {
  convertAnswerValueToString,
  convertStringToFormValue,
} from "./utils/intake-value-converter";

const INTAKE_RESPONSE_MODAL_SIZE: AppModalSize = "2xl";
const INTAKE_RESPONSE_MODAL_CLASSNAME = "w-[calc(100vw-2rem)] max-w-4xl";

const INTAKE_RESPONSE_CREATE_MODAL_FORM_ID =
  "intake-response-create-modal-form";
const INTAKE_RESPONSE_EDIT_MODAL_FORM_ID = "intake-response-edit-modal-form";

/** Match hook copy; shown after `invalidateQueries` so toasts align with modal loading. */
const INTAKE_RESPONSE_CREATED_TOAST = "Matter intake template response created";
const INTAKE_RESPONSE_UPDATED_TOAST = "Matter intake template response updated";
const INTAKE_RESPONSE_DELETED_TOAST = "Matter intake template response deleted";

interface IntakeResponseModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId: string;
}

export const IntakeResponseModal = ({
  open,
  setOpen,
  matterId,
}: IntakeResponseModalProps) => {
  const { hasActionPermission } = useUser();
  const queryClient = useQueryClient();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [formSchema, setFormSchema] =
    useState<z.ZodType<Record<string, unknown>>>();
  /** Covers create mutation + matter refetch (RQ `isPending` ends before `invalidateQueries` finishes). */
  const [isIntakeCreateBusy, setIsIntakeCreateBusy] = useState(false);
  const intakeCreateSubmitLockRef = useRef(false);

  const createIntakeResponse = useCreateMatterIntakeTemplateResponse({
    showSuccessToast: false,
  });

  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const templates = matterSettingsData?.data.intakeTemplates ?? [];

  const { data: questionsResponse, isLoading: isQuestionsLoading } = useQuery({
    ...retrieveMatterIntakeTemplateQuestionsOptions(selectedTemplateId),
    enabled: !!selectedTemplateId,
  });
  const questions = useMemo(
    () => questionsResponse?.data ?? [],
    [questionsResponse?.data],
  );

  const selectedTemplate = templates.find(
    (template) => template.id === selectedTemplateId,
  );

  const form = useForm<Record<string, unknown>>({
    resolver: zodResolver(formSchema || z.object({ templateId: z.string() })),
    mode: "onSubmit",
    defaultValues: {
      templateId: "",
    },
  });

  useEffect(() => {
    if (questions.length > 0) {
      const newSchema = createIntakeResponseSchema(questions);
      setFormSchema(newSchema);
      form.reset({ templateId: selectedTemplateId });
    }
  }, [questions, form, selectedTemplateId]);

  useEffect(() => {
    if (!open) {
      intakeCreateSubmitLockRef.current = false;
      setIsIntakeCreateBusy(false);
      setSelectedTemplateId("");
      setFormSchema(undefined);
      form.reset({ templateId: "" });
    }
  }, [open, form]);

  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIntakeTemplateResponse,
    ACTION_PERMISSION_VALUES.create,
  );

  const onSubmit = async (data: Record<string, unknown>) => {
    if (intakeCreateSubmitLockRef.current) {
      return;
    }
    intakeCreateSubmitLockRef.current = true;
    setIsIntakeCreateBusy(true);
    try {
      const responses = questions.map((question) => {
        const fieldName = `question_${question.id}`;
        const answer = data[fieldName];

        return {
          templateId: question.templateId,
          questionId: question.id,
          answer: convertAnswerValueToString(answer),
        };
      });

      await createIntakeResponse.mutateAsync({
        matterId,
        responses,
      });

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, matterId],
      });

      toast.success(INTAKE_RESPONSE_CREATED_TOAST);
      setOpen(false);
    } catch {
      // Error toast handled by useCreateMatterIntakeTemplateResponse
    } finally {
      intakeCreateSubmitLockRef.current = false;
      setIsIntakeCreateBusy(false);
    }
  };

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    form.setValue("templateId", templateId);
    form.reset({ templateId });
  };

  const renderQuestionField = (question: MatterIntakeTemplateQuestion) => {
    const fieldName = `question_${question.id}`;

    return (
      <div key={fieldName} className="space-y-2">
        {renderIntakeFormField({
          question,
          register: form.register(fieldName),
          fieldName,
        })}
      </div>
    );
  };

  const modalFooter = (
    <div className="flex w-full flex-wrap justify-end gap-2">
      <FormCancelButton setOpen={setOpen} isDirty={form.formState.isDirty} />
      {hasCreatePermission && (
        <FormSubmitButton
          variant="default"
          loading={isIntakeCreateBusy}
          disabled={
            !selectedTemplateId ||
            questions.length === 0 ||
            isQuestionsLoading ||
            isIntakeCreateBusy
          }
          title="Submit response"
          submitFormId={INTAKE_RESPONSE_CREATE_MODAL_FORM_ID}
        />
      )}
    </div>
  );

  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title="Create intake response"
        description="Select a template and respond to the questions"
        size={INTAKE_RESPONSE_MODAL_SIZE}
        className={INTAKE_RESPONSE_MODAL_CLASSNAME}
        loading={isIntakeCreateBusy}
        isDirty={form.formState.isDirty}
        footer={modalFooter}
      >
        <form
          id={INTAKE_RESPONSE_CREATE_MODAL_FORM_ID}
          className="relative flex w-full min-w-0 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit(onSubmit)();
          }}
        >
          {isIntakeCreateBusy && <FormLoadingOverlay />}
          <fieldset className={modalFormFieldsetClassName}>
            <SelectFormField
              label="Select template*"
              register={form.register("templateId")}
              items={templates.map((template) => ({
                value: template.id,
                label: template.label,
              }))}
              itemsKey="value"
              formatItemLabel={(item) => item?.label ?? ""}
              placeholder="Choose an intake template"
              onChange={(value) => {
                if (!value) return;
                handleTemplateChange(value);
              }}
            />
          </fieldset>

          {selectedTemplate && (
            <div className="mt-6 pb-1">
              {isQuestionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : questions.length > 0 ? (
                <fieldset className={modalFormFieldsetClassName}>
                  {questions
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map(renderQuestionField)}
                </fieldset>
              ) : (
                <div className="text-muted-foreground py-8 text-center">
                  <Typography variant="default">
                    No questions found in this template.
                  </Typography>
                </div>
              )}
            </div>
          )}
        </form>
      </AppModal>
    </Form>
  );
};

interface EditIntakeResponseModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  response: MatterIntakeTemplateResponse | null;
  matterId: string;
}

type EditFormValues = z.infer<
  ReturnType<typeof createEditIntakeResponseSchema>
>;

export const EditIntakeResponseModal = ({
  open,
  setOpen,
  response,
  matterId,
}: EditIntakeResponseModalProps) => {
  const { hasActionPermission } = useUser();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isIntakeEditBusy, setIsIntakeEditBusy] = useState(false);
  const intakeEditSubmitLockRef = useRef(false);

  const editSchema = useMemo(() => {
    if (!response) {
      return z.object({ answer: z.string().optional() });
    }
    return createEditIntakeResponseSchema(response.question?.type || "");
  }, [response]);

  const updateIntakeResponse = useUpdateMatterIntakeTemplateResponse({
    showSuccessToast: false,
  });
  const deleteIntakeResponse = useDeleteMatterIntakeTemplateResponse({
    showSuccessToast: false,
  });

  const form = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    mode: "onSubmit",
    defaultValues: {
      answer: "",
    },
  });

  /**
   * When the modal closes we clear the answer. Re-opening the same row does not change
   * `response` by reference, so we must re-apply values whenever `open` becomes true.
   */
  useEffect(() => {
    if (!open || !response) {
      return;
    }
    const defaultValue = convertStringToFormValue(
      response.answer ?? undefined,
      response.question?.type || "",
    );
    form.reset({ answer: defaultValue });
  }, [open, response, form]);

  useEffect(() => {
    if (!open) {
      intakeEditSubmitLockRef.current = false;
      setIsIntakeEditBusy(false);
      setShowDeleteConfirm(false);
      form.reset({ answer: "" });
    }
  }, [open, form]);

  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIntakeTemplateResponse,
    ACTION_PERMISSION_VALUES.update,
  );

  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIntakeTemplateResponse,
    ACTION_PERMISSION_VALUES.delete,
  );

  const handleDelete = async () => {
    if (!response) return;

    try {
      await deleteIntakeResponse.mutateAsync({ id: response.id });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, matterId],
      });
      toast.success(INTAKE_RESPONSE_DELETED_TOAST);
      setOpen(false);
      setShowDeleteConfirm(false);
    } catch {
      // Error toast handled by useDeleteMatterIntakeTemplateResponse
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const onSubmit = async (data: EditFormValues) => {
    if (!response) return;
    if (intakeEditSubmitLockRef.current) return;

    intakeEditSubmitLockRef.current = true;
    setIsIntakeEditBusy(true);
    try {
      const answerValue = convertAnswerValueToString(data.answer);

      await updateIntakeResponse.mutateAsync({
        matterId,
        id: response.id,
        questionId: response.questionId,
        answer: answerValue,
        dateModified: response.dateModified ?? "",
      });

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, matterId],
      });

      toast.success(INTAKE_RESPONSE_UPDATED_TOAST);
      setOpen(false);
    } catch {
      // Error toast handled by useUpdateMatterIntakeTemplateResponse
    } finally {
      intakeEditSubmitLockRef.current = false;
      setIsIntakeEditBusy(false);
    }
  };

  const renderQuestionField = () => {
    if (!response?.question) return null;

    return (
      <div className="space-y-2">
        {renderIntakeFormField({
          question: response.question,
          register: form.register("answer"),
        })}
      </div>
    );
  };

  if (!response) {
    return null;
  }

  const modalFooter = (
    <div className="flex w-full flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        {hasDeletePermission && (
          <Button
            variant="destructive"
            type="button"
            onClick={handleDeleteClick}
            disabled={isIntakeEditBusy || deleteIntakeResponse.isPending}
          >
            Delete
          </Button>
        )}
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <FormCancelButton setOpen={setOpen} isDirty={form.formState.isDirty} />
        {hasUpdatePermission && (
          <FormSubmitButton
            variant="default"
            loading={isIntakeEditBusy}
            disabled={!response.question || isIntakeEditBusy}
            title="Update response"
            submitFormId={INTAKE_RESPONSE_EDIT_MODAL_FORM_ID}
          />
        )}
      </div>
    </div>
  );

  return (
    <Fragment>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title="Edit intake response"
          size={INTAKE_RESPONSE_MODAL_SIZE}
          className={INTAKE_RESPONSE_MODAL_CLASSNAME}
          loading={isIntakeEditBusy}
          isDirty={form.formState.isDirty}
          footer={modalFooter}
        >
          <form
            id={INTAKE_RESPONSE_EDIT_MODAL_FORM_ID}
            className="relative flex w-full min-w-0 flex-col"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit(onSubmit)();
            }}
          >
            {isIntakeEditBusy && <FormLoadingOverlay />}
            <fieldset className={modalFormFieldsetClassName}>
              {renderQuestionField()}
            </fieldset>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        open={showDeleteConfirm}
        setOpen={setShowDeleteConfirm}
        title="Delete intake response"
        description="Are you sure you want to delete this intake response? This action cannot be undone."
        buttonText={deleteIntakeResponse.isPending ? "Deleting..." : "Delete"}
        isActionPending={deleteIntakeResponse.isPending}
        afterActionFn={handleDelete}
      />
    </Fragment>
  );
};
